/*
 * Product page 2026 - <badge-tooltip>
 *
 * Wraps a product badge that carries description copy. The tooltip opens on
 * hover and keyboard focus on pointer devices, and on tap on touch devices.
 * The copy stays in the DOM at all times so `aria-describedby` on the trigger
 * keeps describing the badge to screen readers whether or not it is visible.
 */

(() => {
  if (customElements.get('badge-tooltip')) return;

  const VIEWPORT_MARGIN = 12;
  const openTooltips = new Set();

  const closeAllExcept = (keep) => {
    openTooltips.forEach((tooltip) => {
      if (tooltip !== keep) tooltip.close();
    });
  };

  class BadgeTooltip extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('button');
      this.content = this.querySelector('.badge-tooltip__content');
      if (!this.trigger || !this.content) return;

      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.lastPointerType = null;
      this.wasOpenOnPointerDown = false;

      this.addEventListener('pointerdown', this.handlePointerDown);
      this.trigger.addEventListener('click', this.handleClick);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('keydown', this.handleKeyDown);

      if (this.canHover) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
      }
    }

    disconnectedCallback() {
      this.close();
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.trigger?.removeEventListener('click', this.handleClick);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('keydown', this.handleKeyDown);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
    }

    get open() {
      return this.hasAttribute('data-open');
    }

    handlePointerDown = (event) => {
      this.lastPointerType = event.pointerType;

      // A tap focuses the trigger before it clicks it, and focus already opens the
      // tooltip. Toggling off `this.open` at click time would therefore close it on
      // the very first tap, so the toggle reads the state from before the tap.
      this.wasOpenOnPointerDown = this.open;
    };

    handleClick = (event) => {
      const keyboardActivated = event.detail === 0;
      const isTouch = !this.canHover || this.lastPointerType === 'touch' || this.lastPointerType === 'pen';

      if (keyboardActivated) {
        if (this.open) this.close();
        else this.show();
      } else if (isTouch && this.wasOpenOnPointerDown) {
        this.close();
      } else {
        // Hover already handles pointer devices; the click only needs to keep it open.
        this.show();
      }

      this.wasOpenOnPointerDown = false;
    };

    handleMouseEnter = () => this.show();

    handleMouseLeave = () => this.close();

    handleFocusIn = () => this.show();

    handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    };

    handleKeyDown = (event) => {
      if (event.key !== 'Escape' || !this.open) return;
      event.stopPropagation();
      this.close();
      this.trigger.focus();
    };

    show() {
      if (this.open) return;

      closeAllExcept(this);
      this.setAttribute('data-open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
      openTooltips.add(this);
      this.keepInViewport();

      document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
      window.addEventListener('resize', this.handleViewportChange);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
    }

    close() {
      if (!this.open) return;

      this.removeAttribute('data-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      openTooltips.delete(this);

      document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('scroll', this.handleViewportChange);
    }

    handleDocumentPointerDown = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    handleViewportChange = () => this.keepInViewport();

    /** Nudge the tooltip horizontally so it never overflows the viewport. */
    keepInViewport() {
      this.style.setProperty('--badge-tooltip-shift', '0px');

      const bounds = this.content.getBoundingClientRect();
      const overflowEnd = bounds.right - (window.innerWidth - VIEWPORT_MARGIN);
      const overflowStart = VIEWPORT_MARGIN - bounds.left;

      let shift = 0;
      if (overflowEnd > 0) shift = -overflowEnd;
      else if (overflowStart > 0) shift = overflowStart;

      if (shift !== 0) this.style.setProperty('--badge-tooltip-shift', `${Math.round(shift)}px`);
    }
  }

  customElements.define('badge-tooltip', BadgeTooltip);
})();

/*
 * Product page 2026 - free gift "Switch to <variant>" button
 *
 * The free gift callout offers a button that selects the variant carrying the gift. It checks
 * that variant's option radios and fires a bubbling `change`, which the theme's <variant-picker>
 * listens for on document.body, so the usual variant change and re-render run unchanged.
 */

(() => {
  if (window.pdp2026FreeGiftSwitch) return;
  window.pdp2026FreeGiftSwitch = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-switch-variant]');
    if (!button) return;

    const formId = button.getAttribute('data-form-id');
    const ids = (button.getAttribute('data-option-value-ids') || '').split(',').filter(Boolean);

    ids.forEach((id) => {
      const input = document.querySelector(
        `input[data-option-position][form="${CSS.escape(formId)}"][value="${CSS.escape(id)}"]`
      );

      if (!input || input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
})();
